// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { ButtonHTMLAttributes, ReactNode, Ref, SelectHTMLAttributes } from 'react';

import { cx } from './cx';
import styles from './Select.module.css';

export type SelectVariant = 'filled' | 'outline';
/**
 * `sm` — dense (28px): forms, dialogs, table cells, settings, legacy screens.
 * `md` — bar (32px): toolbars, the PartBar, the BulkBar.
 * `lg` — field (32px, body text): the inspector's FieldRow controls.
 */
export type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'prefix'> {
  /** `filled`: a borderless tinted well (Bygning). `outline`: a sunken bordered field. */
  variant?: SelectVariant;
  size?: SelectSize;
  /** Fill the container's width. */
  block?: boolean;
  /**
   * A muted inline caption inside the well, before the value — pass a
   * `<label htmlFor>` so it also names the select (Tabel's Skabelon). The
   * wrapper then draws the well instead of the select.
   */
  prefix?: ReactNode;
  ref?: Ref<HTMLSelectElement>;
}

export interface SelectButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: SelectVariant;
  size?: SelectSize;
  block?: boolean;
  /** The button shows a prompt ("Vælg …") rather than a value: muted text. */
  placeholder?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

function Chevron() {
  return (
    <svg className={styles.chevron} aria-hidden="true" viewBox="0 0 16 16">
      <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function isInvalid(v: SelectHTMLAttributes<HTMLSelectElement>['aria-invalid']): boolean {
  return v === true || v === 'true';
}

/**
 * The shared native dropdown: a `<select>` in a wrapper that draws the
 * chevron. `className` and `style` land on the wrapper (the one width rule);
 * the select fills it. Every other prop goes to the `<select>`.
 *
 * Not to be confused with `SelectDropdown` / `MultiSelectDropdown`, the
 * tag-chip pickers for select/multiselect columns — those stay as they are.
 */
export function Select({ variant = 'outline', size = 'sm', block, prefix, className, style, disabled, children, ...rest }: SelectProps) {
  const hasPrefix = prefix != null && prefix !== false;
  return (
    <span
      className={cx(styles.wrap, styles[variant], styles[size], block && styles.block, hasPrefix && styles.withPrefix, className)}
      style={style}
      data-disabled={disabled || undefined}
      data-invalid={isInvalid(rest['aria-invalid']) || undefined}
    >
      {hasPrefix && <span className={styles.prefix}>{prefix}</span>}
      <select className={styles.select} disabled={disabled} {...rest}>
        {children}
      </select>
      {!rest.multiple && <Chevron />}
    </span>
  );
}

/**
 * A button that looks like `Select`, for custom pickers that open their own
 * list (the inspector's Opbygning). Same variants, sizes and width rule.
 */
export function SelectButton({
  variant = 'outline',
  size = 'sm',
  block,
  placeholder,
  className,
  style,
  disabled,
  children,
  ...rest
}: SelectButtonProps) {
  return (
    <span
      className={cx(styles.wrap, styles[variant], styles[size], block && styles.block, className)}
      style={style}
      data-disabled={disabled || undefined}
      data-invalid={isInvalid(rest['aria-invalid']) || undefined}
    >
      <button type="button" className={cx(styles.select, styles.button, placeholder && styles.placeholder)} disabled={disabled} {...rest}>
        <span className={styles.text}>{children}</span>
      </button>
      <Chevron />
    </span>
  );
}
